/* eslint-disable no-nested-ternary */
import React from 'react';
import { useTranslation } from 'Shared/Hooks/useTranslation';
import _throttle from 'lodash/throttle';

import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';

import checkIfItemIsInView from 'Jsx/Utils/checkIfItemIsInView';
import mixpanel from 'Jsx/Utils/mixpanelTracking';

import useCanEnrollWithFirstEnroll from 'Jsx/CourseDetail/hooks/useCanEnrollWithFirstEnroll';

import CourseDetailPrice from 'Jsx/CourseDetail/components/CourseDetailPrice';

import * as EVENTS from '../events';
import useCoursePromoCountdown from '../hooks/useCoursePromoCountdown';
import locales from '../i18n/localeEs';

const translations = locales.es;

type StickyBottomBarProp = {
  className?: string;
  loadingCanEnroll?: boolean;
  showPaymentDialog: () => void;
  BuyButtons: JSX.Element;
  isFreeCourse: boolean;
  canEnroll: boolean;
  courseDueDate: string;
};

const StickyBottomBar: React.FC<StickyBottomBarProp> = ({
  className,
  loadingCanEnroll,
  showPaymentDialog,
  courseDueDate,
  BuyButtons,
  isFreeCourse,
  canEnroll,
}) => {
  const { canEnrollToCourseFreemium } = useCanEnrollWithFirstEnroll();

  const { t } = useTranslation();
  const { promoRemainingTime } = useCoursePromoCountdown(courseDueDate);
  const [fixed, setFixed] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = _throttle(() => {
      const upsellingSection = document.getElementById(
        'course-detail-upselling-section',
      );
      const priceSection = document.getElementById(
        'course-detail-transaction-section',
      );

      const shouldShowStickyButton =
        !checkIfItemIsInView(upsellingSection) &&
        !checkIfItemIsInView(priceSection);

      setFixed(shouldShowStickyButton);
    }, 100);

    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (
    loadingCanEnroll ||
    (canEnroll && !isFreeCourse) ||
    canEnrollToCourseFreemium
  ) {
    return null;
  }

  return (
    <div
      className={`${className} bg-white ${
        fixed
          ? 'fixed flex flex-wrap justify-center sm:justify-around'
          : 'hidden'
      } z-5 bottom-0 left-0 w-full p-12 text-center`}
      style={{ boxShadow: 'rgba(0, 0, 0, 0.05) 2px 0px 4px' }}
    >
      <div>
        {promoRemainingTime && (
          <p className="mt-12 text-center font-link text-red-main">
            {t('OFFER_END_IN', {
              defaultValue: translations.OFFER_END_IN,
            })}{' '}
            {promoRemainingTime}!
          </p>
        )}
        <CourseDetailPrice realPriceColor="gray-dark" />
      </div>
      <div className="mx-auto sm:mx-0 sm:mr-0">
        {!canEnroll ? (
          <PrimaryButton
            full
            label={t('BUY_NOW', {
              defaultValue: translations.BUY_NOW,
            })}
            onClick={() => {
              mixpanel.track(EVENTS.BUY_FROM_STICKY, {});
              showPaymentDialog();
            }}
          />
        ) : null}
        {isFreeCourse ? BuyButtons : null}
      </div>
    </div>
  );
};

export default StickyBottomBar;
